




import Enumerable from "linq";
import { Component, Mixins, Prop } from "vue-property-decorator";
import {
  ExternalValidationResultAlert,
  ExternalValidationResults,
  IDocumentAlert,
  RequestedObject,
} from "../../../../domain/document";
import {
  EmptyValidator,
  NumericRangeValidator,
  TextValidator,
  CalculatedValueValidator,
  DocumentAttributesValidator,
  ExternalValidationObjectValidator,
  CompareDateValidator,
} from "../../../../domain/document/validators";
import {
  AbstractEmptyAlert,
  AbstractInputRuleAlert,
  CalculationAlert,
  CompareDateAlert,
  CompareDateEmptyAlert,
  TextLengthAlert,
} from "../../../../domain/document/alerts";
import { ModelEditableMixin } from "../../../system";
import { IConfirmDialogOptions } from "../../../system/dialogs";
import { EditingDocument } from "..";

@Component
export default class DocumentValidator extends Mixins(ModelEditableMixin) {
  @Prop()
  validateWithExternalValidationUrl: () => Promise<ExternalValidationResults>;

  editingSource: EditingDocument;
  writePropertyWhenValid: (writer: (source: EditingDocument) => void) => void;

  get shouldCorrectTextLengthAlert(): boolean {
    return this.$configuration.behavior.document.editor.validator.textLength.isError;
  }

  async validate(isRegistration: boolean): Promise<{ isOk: boolean, requestedObjects: RequestedObject[] }> {
    const results = await this.executeValidation();
    const isOk = !this.containsError(results.alerts) && await this.confirmAlerts(results.alerts, isRegistration);
    this.writePropertyWhenValid(s => s.alerts = results.alerts);
    await this.$nextTick(); // 呼出元で更新したalertsを参照できるように.
    return {
      isOk,
      requestedObjects: results.objects,
    };
  }

  public containsEmptyAlert(): boolean {
    const validator = new EmptyValidator();
    const alerts = validator.validate(this.editingSource.source);
    return alerts.length !== 0;
  }

  private async getValidatedResults(): Promise<ExternalValidationResults> {
    try {
      if (this.validateWithExternalValidationUrl) {
        return await this.validateWithExternalValidationUrl();
      }
      return new ExternalValidationResults([], []);
    } catch (ex) {
      // errorはvalidate()で出す。
      return null;
    }
  }

  private async executeValidation(): Promise<{ alerts: IDocumentAlert[], objects: RequestedObject[] }> {
    const results = await this.getValidatedResults();
    return {
      alerts: this.findAlerts(results.alerts),
      objects: results.objects,
    };
  }

  private findAlerts(externalAlerts: ExternalValidationResultAlert[]): IDocumentAlert[] {
    return Enumerable.from([
      new DocumentAttributesValidator(),
      new EmptyValidator(),
      new NumericRangeValidator(),
      this.$configuration.behavior.document.editor.shouldConfirmCalculationErrorToRegisterDocument ? new CalculatedValueValidator() : null,
      new TextValidator(),
      new ExternalValidationObjectValidator(externalAlerts),
      new CompareDateValidator(),
    ])
    .where(v => !!v)
    .select(v => v.validate(this.editingSource.source))
    .aggregate([] as IDocumentAlert[], (s, a) => s.concat(a));
  }

  private containsError(alerts: IDocumentAlert[]): boolean {
    return Enumerable.from(alerts).any(a => a.isError);
  }

  private async confirmAlerts(alerts: IDocumentAlert[], isRegistration: boolean): Promise<boolean> {
    // 直接returnする形だとコンパイル時に最後の判定のawaitが不要とのアラートが出る
    // &&の仕様によって不要となるようだが、紛らわしいのでいったん変数を経由してアラートを回避している
    // 参考 https://qiita.com/Imamotty/items/bc659569239379dded55
    // 【expr1 && expr2】
    // ・expr1 を false と見ることができる場合は、expr1 を返します。
    // ・そうでない場合は、expr2 を返します。
    const confirmResult = (await this.confirmCalculateAlerts(alerts, isRegistration))
        && (await this.confirmInputRuleAlerts(alerts, isRegistration))
        && (await this.confirmCompareDateAlerts(alerts, isRegistration))
        && (await this.confirmIgnorableEmptyAlerts(alerts, isRegistration));
    return confirmResult;
  }

  private async confirmCalculateAlerts(alerts: IDocumentAlert[], isRegistration: boolean): Promise<boolean> {
    return this.isCalculatedValueAlertConfirmed(
      Enumerable.from(alerts)
        .where(a => a instanceof CalculationAlert)
        .toArray(), isRegistration);
  }

  private async confirmInputRuleAlerts(alerts: IDocumentAlert[], isRegistration: boolean): Promise<boolean> {
    // TODO:旧WebDocuでは文字コードの判定は有効になっていなかった機能なので、文字の判定の信頼性が低い？
    // 登録できないと大変なので、とりあえずは確認ダイアログを出して続行はできるようにしておく。
    return this.isTextValidationAlertConfirmed(
      Enumerable.from(alerts)
        .where(a => a instanceof AbstractInputRuleAlert)
        .toArray(), isRegistration);
  }

  private async confirmCompareDateAlerts(alerts: IDocumentAlert[], isRegistration: boolean): Promise<boolean> {
    const targetAlerts = Enumerable.from(alerts)
      .where(a => a instanceof CompareDateAlert || a instanceof CompareDateEmptyAlert)
      .toArray();
    return this.isAlertsConfirmed(targetAlerts, isRegistration);
  }

  private async confirmIgnorableEmptyAlerts(alerts: IDocumentAlert[], isRegistration: boolean): Promise<boolean> {
    const targetAlerts = Enumerable.from(alerts)
        .where(a => a instanceof AbstractEmptyAlert && !a.isError)
        .toArray();
    return this.isAlertsConfirmed(targetAlerts, isRegistration);
  }

  private async isTextValidationAlertConfirmed(alerts: IDocumentAlert[], isRegistration: boolean): Promise<boolean> {
    try {
      if (!alerts || (alerts.length <= 0)) {
        return true;
      }
      if (this.shouldCorrectTextLengthAlert && this.areContainTextLengthAlerts(alerts)) {
        alerts.forEach(a => a.isError = a.isError || (a instanceof TextLengthAlert));
        return false;
      }
      if (isRegistration) {
        const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
          "入力規則",
          ["入力規則に一致していない項目があります。無視して登録しても本当によろしいですか？"],
          "無視して登録する"
        );
        if (index === 0) { return true; }
      }
    } catch {
    }
    alerts.forEach(a => a.isConfirmDenied = true);
    return false;
  }

  private areContainTextLengthAlerts(alerts: IDocumentAlert[]): boolean {
    return Enumerable.from(alerts).any(a => a instanceof TextLengthAlert);
  }

  private async isCalculatedValueAlertConfirmed(alerts: IDocumentAlert[], isRegistration: boolean): Promise<boolean> {
    try {
      if (!alerts || (alerts.length === 0)) {
        return true;
      }
      if (!isRegistration) {
        return false;
      }
      for (const alert of alerts) {
        const index = await this.showConfirmDialogAsync({
          message:  [
            alert.target + "には" + alert.description,
            "計算せずに未入力で書類を登録してもよろしいですか？"
          ],
          buttons: [
            { kind: "danger", label: "無視して登録する"},
            { kind: "danger", label: "全て無視して登録する"},
          ],
          cancellation: {
            isEnable: true,
          },
        });
        if (index === null || index === undefined) {
          // 「中止する」
          alert.isConfirmDenied = true;
          return false;
        }
        if (index === 1) {
          // 「全て無視して登録する」
          return true;
        }
      }
      return true;
    } catch {
    }
    return false;
  }

  private async isAlertsConfirmed(alerts: IDocumentAlert[], isRegistration: boolean): Promise<boolean> {
    try {
      if (!alerts || (alerts.length <= 0)) {
        return true;
      }
      if (isRegistration) {
        for (const alert of alerts) {
          const index = await this.showConfirmDialogAsync({
            message:  [
              alert.target + "は" + alert.description,
              "無視して書類を登録してもよろしいですか？"
            ],
            buttons: [
              { kind: "danger", label: "無視して登録する"},
              { kind: "danger", label: "全て無視して登録する"},
            ],
            cancellation: {
              isEnable: true,
            },
          });
          if (index === null || index === undefined) {
            // 「中止する」
            alert.isConfirmDenied = true; 
            return false;
          }
          if (index === 1) {
            // 「全て無視して登録する」
            return true;
          }
        }
        return true;
      }
    } catch (e) {
      this.$logger.error(e);
    }
    return false;
  }

  private async showConfirmDialogAsync(option: IConfirmDialogOptions): Promise<number> {
    return this.$commonDialogs.confirmation.show(option);
  }

  isLinkedExternalScriptProcessing(): boolean {
    return !!this.editingSource && (this.editingSource.externalScriptRunner.isProcessing || this.editingSource.externalScriptRunner.hasRequestedScripts);
  }
}
